import { secondaryStyles } from "@/components/Form/InputFields/secondaryStyles";
import { DATE_FORMAT_TWO } from "@/constants";
import { formFieldError, InputFieldType } from "@/types";
import convertDateToTimezone from "@/utils/convertDateToTimezone";
import ensureTimezone from "@/utils/ensureTimezone";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/system";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useController, UseControllerProps } from "react-hook-form";

export default function CustomDateTimePicker({
  name,
  control,
  label = null,
  error,
  datePickerProps,
  type = "primary",
  required = false,
  timezone,
  isLoading = false,
  isClearable = true,
  defaultEndOfDay = false,
}: CustomDateTimePickerProps) {
  const theme = useTheme();

  const { field } = useController({
    control,
    name,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en"}>
      <DateTimePicker
        label={label}
        value={field.value ? field.value : null}
        onChange={(newValue) => {
          if (!newValue) return null;
          // End-date fields default a freshly-picked day to end-of-day (11:59 PM)
          // instead of midnight, so the range covers the whole day.
          let picked = newValue;
          if (
            defaultEndOfDay &&
            dayjs.isDayjs(picked) &&
            picked.hour() === 0 &&
            picked.minute() === 0 &&
            picked.second() === 0
          ) {
            picked = picked.hour(23).minute(59).second(0);
          }
          if (timezone) {
            const timezoneEnsuredValue = field.value
              ? picked
              : ensureTimezone(
                  picked.format(
                    // Keep the time when defaulting to end-of-day; DATE_FORMAT_TWO is date-only.
                    defaultEndOfDay ? "YYYY-MM-DD HH:mm:ss" : DATE_FORMAT_TWO
                  ),
                  timezone
                );
            const value = convertDateToTimezone(timezoneEnsuredValue, timezone);
            field.onChange(value ? value : null);
          } else {
            const value = dayjs.isDayjs(picked) ? picked : dayjs(picked);
            field.onChange(value);
          }
        }}
        onAccept={(newValue) => {
          if (!newValue) field.onChange(null);
        }}
        renderInput={(params) => {
          const { inputProps, ...restParams } = params;
          const value = inputProps?.value;

          return (
            <TextField
              {...restParams}
              required={required}
              fullWidth
              helperText={error && typeof error === "string" ? error : ""}
              FormHelperTextProps={{
                error: true,
              }}
              sx={{
                ...secondaryStyles(type, theme, false, true),
              }}
              inputProps={{
                ...inputProps,
                value,
                readOnly: true,
              }}
            />
          );
        }}
        loading={isLoading}
        componentsProps={{
          actionBar: {
            actions: isClearable ? ["clear"] : [],
          },
        }}
        {...datePickerProps}
      />
    </LocalizationProvider>
  );
}

interface CustomDateTimePickerProps extends UseControllerProps {
  label?: string | null;
  error?: formFieldError;
  datePickerProps?: Partial<DateTimePickerProps<any, any>>;
  type?: InputFieldType;
  required?: boolean;
  disabled?: boolean;
  timezone?: string;
  isLoading?: boolean;
  isClearable?: boolean;
  // When true, a freshly-picked day defaults to 11:59 PM instead of midnight (for end dates).
  defaultEndOfDay?: boolean;
}
